import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";
import { formatPopularity } from "@/utils/content";

interface DramaCardProps {
  drama: Drama;
  variant?: "grid" | "list" | "compact";
}

export default function DramaCard({ drama, variant = "grid" }: DramaCardProps) {
  if (variant === "list") {
    return (
      <Link
        to={`/detail/${drama.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4"
      >
        <div className="h-32 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-36 md:w-28">
          <img
            src={drama.cover}
            alt={`${drama.title} 封面`}
            title={`${drama.title} 剧集介绍与解析`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 md:text-lg">
              {drama.title}
            </h3>
            <span className="flex shrink-0 items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
              <i className="ri-star-fill"></i>
              {drama.rating}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-900">{drama.category}</span>
            <span>{drama.genre}</span>
            <span>{drama.year}</span>
            <span>{drama.episodes > 1 ? `${drama.episodes}集` : "电影"}</span>
          </div>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{drama.synopsis}</p>
          <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-fire-line"></i></span>
              {formatPopularity(drama.popularity)}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-calendar-line"></i></span>
              {drama.publishDate}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${drama.id}`}
        className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={drama.cover}
            alt={`${drama.title} 封面`}
            title={`${drama.title} 剧集介绍`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
            {drama.title}
          </h3>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-accent-700">{drama.rating}分</span>
            <span>·</span>
            <span className="truncate">{drama.genre}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${drama.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={drama.cover}
          alt={`${drama.title} 封面`}
          title={`${drama.title} 剧集介绍与解析`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-secondary-950/70 px-2 py-0.5 text-xs text-background-50">
          {drama.category}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-accent-950">
          <i className="ri-star-fill"></i>
          {drama.rating}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3 md:p-4">
        <h3 className="truncate font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 md:text-base">
          {drama.title}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
          <span>{drama.genre}</span>
          <span>·</span>
          <span>{drama.year}</span>
        </div>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{drama.synopsis}</p>
      </div>
    </Link>
  );
}